import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { STATUS_COLORS, STATUS_FALLBACK_COLOR } from '@constants/index';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Status pill color (Title Case keys). */
export const statusColor = (status?: string | null): string =>
  STATUS_COLORS[status || ''] || STATUS_FALLBACK_COLOR;

/** 10% tint of a status color ("rgb(r,g,b)" or "#rrggbb"). */
export const colorTint = (color: string): string => {
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', ',0.1)');
  return `${color}1A`;
};

/** Legacy status keys (onGoing, inComplete, ...) -> Title Case labels. */
export const titleCase = (value?: string | null): string => {
  if (!value) return '';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Parses API values: ISO datetimes as-is, bare 'YYYY-MM-DD' dates as LOCAL midnight. */
export const parseDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** MM/DD/YYYY */
export const shortDateFormat = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
};

/** MM/DD/YYYY in UTC (the legacy `moment.utc(value).format('MM/DD/YYYY')` for datetime columns). */
export const shortDateUtcFormat = (value?: string | Date | null): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m && value.length === 10) return `${m[2]}/${m[3]}/${m[1]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}/${d.getUTCFullYear()}`;
};

/** MM/DD/YYYY HH:mm */
export const dateTimeFormat = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return `${shortDateFormat(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** "Mar 05, 2026" (legacy moment 'MMM DD, YYYY'). */
export const legacyMediumDate = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  return `${month} ${pad(d.getDate())}, ${d.getFullYear()}`;
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** "Mar 5th, 2026" (legacy moment 'MMM Do, YYYY'). */
export const legacyOrdinalDate = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  return `${month} ${ordinal(d.getDate())}, ${d.getFullYear()}`;
};

/** Month D, YYYY (the long form used on view pages). */
export const longDateFormat = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/** Date -> 'YYYY-MM-DD' (local calendar day) for DATE columns. */
export const toDateOnly = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Date -> ISO string, null when empty. */
export const toIso = (value?: string | Date | null): string | null => {
  const d = parseDate(value);
  return d ? d.toISOString() : null;
};

export const addDays = (value: string | Date, days: number): Date => {
  const d = parseDate(value) ?? new Date();
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

/** Whole days from today to the date (negative when past). */
export const daysUntil = (value?: string | Date | null): number | null => {
  const d = parseDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
};

/**
 * Progressive ###-###-#### phone mask: strips non-digits, caps at 10 digits,
 * formats as far as the input goes.
 */
export const phoneFormat = (value?: string | null): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const emailOk = (value?: string | null): boolean =>
  !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** "First Last" from the joined audit/account columns. */
export const personName = (row?: {
  pvFirstName?: string | null;
  pvLastName?: string | null;
  pvUserName?: string | null;
  fullName?: string | null;
}): string => {
  if (!row) return '';
  const joined = `${row.pvFirstName || ''} ${row.pvLastName || ''}`.trim();
  return joined || row.fullName || row.pvUserName || '';
};

export const filterVoid = <T extends { pvVoid?: number | null }>(rows?: T[] | null): T[] =>
  (rows || []).filter((r) => !r.pvVoid);

/** Numeric form value -> int or null. */
export const toInt = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

/**
 * A sorted copy of rows held in the browser, by one field: numbers by value, anything else as
 * text (a missing value first ascending). Equal rows keep their order.
 */
export const sortRows = <T extends object>(
  rows: readonly T[],
  field: string,
  order: 'asc' | 'desc',
): T[] => {
  const dir = order === 'asc' ? 1 : -1;
  const valueOf = (row: T) => (row as Record<string, unknown>)[field];
  return [...rows].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
  });
};

/** Bytes -> "1.2 MB". */
export const formatBytes = (bytes?: number | string | null): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

/** CSV export of a column set (client-side alternative to the server print). */
export const exportToCsv = (
  filename: string,
  columns: { title: string; path: string }[],
  rows: Record<string, unknown>[],
  formatCell?: (path: string, row: Record<string, unknown>) => string,
) => {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.title)).join(',');
  const lines = rows.map((row) =>
    columns
      .map((c) => esc(formatCell ? formatCell(c.path, row) : String(row[c.path] ?? '')))
      .join(','),
  );
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** The Excel (.xlsx) media type, for the grids' server-side print. */
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Download a base64 payload (the grid's server-side Excel print). */
export const downloadBase64 = (
  fileName: string,
  base64: string,
  mime = 'application/octet-stream',
) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

/** Extracts a readable message from an RTK Query error. */
export const errorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  const e = error as {
    data?: { error?: string; message?: string };
    error?: string;
    message?: string;
  };
  return e?.data?.error || e?.data?.message || e?.error || e?.message || fallback;
};
